import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Flame, Loader2, Send } from "lucide-react";
import { Reveal, SectionHeading } from "./Atoms";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PrayerForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    request: "",
    private: true,
    join_updates: false,
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.request.trim()) {
      toast.error("Please fill in your name, email, and prayer request.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/prayer-requests`, form);
      toast.success("Your prayer request has been received. We are standing with you.");
      setForm({ name: "", email: "", phone: "", request: "", private: true, join_updates: false });
    } catch (err) {
      toast.error("We couldn't submit right now. Please try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="prayer" className="relative py-28 px-6 bg-[#050505]">
      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <Reveal>
            <SectionHeading
              overline="Stand With Us in Prayer"
              title={<>Request <span className="italic gold-text">Prayer</span></>}
              subtitle="We believe in the power of prayer, intercession, deliverance, healing, and the name of Jesus Christ. Submit your prayer request below. Private requests will be handled with care by ministry leadership."
            />
          </Reveal>
          <Reveal delay={100}>
            <div className="pl-5 border-l border-[#C9A44C]/40">
              <p className="font-serif-display italic text-xl sm:text-2xl text-[#F6F0E6] leading-snug">
                “And all things, whatsoever ye shall ask in prayer, believing, ye shall receive.”
              </p>
              <p className="mt-3 text-[#C9A44C] text-xs tracking-[0.22em] uppercase">
                Matthew 21:22 · KJV
              </p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal>
            <form
              onSubmit={submit}
              className="bento-card !p-7 sm:!p-10 space-y-6"
              data-testid="prayer-form"
            >
              <span className="glow-blob" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="field-label" htmlFor="pr-name">Name</label>
                  <input
                    id="pr-name"
                    data-testid="prayer-name"
                    className="input-fire"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="pr-email">Email</label>
                  <input
                    id="pr-email"
                    data-testid="prayer-email"
                    type="email"
                    className="input-fire"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="field-label" htmlFor="pr-phone">Phone <span className="opacity-60 normal-case tracking-normal">(optional)</span></label>
                <input
                  id="pr-phone"
                  data-testid="prayer-phone"
                  className="input-fire"
                  placeholder="(optional)"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                />
              </div>
              <div>
                <label className="field-label" htmlFor="pr-request">Prayer Request</label>
                <textarea
                  id="pr-request"
                  data-testid="prayer-request"
                  className="input-fire"
                  placeholder="Share what's on your heart…"
                  value={form.request}
                  onChange={(e) => update("request", e.target.value)}
                  required
                  rows={5}
                />
              </div>

              <div className="space-y-3 pt-2">
                <label className="check-wrap">
                  <input
                    type="checkbox"
                    data-testid="prayer-private"
                    checked={form.private}
                    onChange={(e) => update("private", e.target.checked)}
                  />
                  Keep this request private — handled by ministry leadership only.
                </label>
                <label className="check-wrap">
                  <input
                    type="checkbox"
                    data-testid="prayer-join-updates"
                    checked={form.join_updates}
                    onChange={(e) => update("join_updates", e.target.checked)}
                  />
                  Also add me to RFMG Updates.
                </label>
              </div>

              <button
                type="submit"
                className="btn-fire w-full sm:w-auto"
                disabled={loading}
                data-testid="prayer-submit"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Flame className="h-4 w-4" strokeWidth={1.7} />}
                {loading ? "Submitting…" : "Submit Prayer Request"}
              </button>

              <p className="text-xs text-[#D9D9D9]/60 pt-3">
                Your prayer request is kept private. We will never display it publicly.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default PrayerForm;
